// @djsem/analysis-contract — Asset Manager (Prompt 21R).
// Asset RECORD lifecycle (source/generated/uploaded, variants, version history, status,
// rights) ABOVE the tested 12D ShowPackPackagingService — packaging consumes these records
// via toShowPackAsset(); one packaging system, no fork. Rights are declared, never assumed.

import { deterministicHash } from "./prepWorkspace";
import { SeqCounter } from "@djsem/shared";
import type { DJSEMShowPackAsset, DJSEMShowPackAssetType } from "./showPackPackaging";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** How an asset record's content originated. */
export type DJSEMAssetOrigin = "source" | "generated" | "uploaded";
/** Review lifecycle status of an asset record. */
export type DJSEMAssetStatus = "draft" | "review" | "approved" | "archived";

/** A single named variant (size/format) of an asset's content. */
export interface DJSEMAssetVariant { id: string; label: string; path?: string; r2Key?: string; hash: string; format?: string; }
/** A recorded historical version of an asset's content hash. */
export interface DJSEMAssetVersion { version: number; hash: string; createdAt: string; note?: string; }

/** The full lifecycle record for a single asset: variants, version history, status, and declared rights. */
export interface DJSEMAssetRecord {
  id: string;
  title: string;
  assetType: DJSEMShowPackAssetType;
  origin: DJSEMAssetOrigin;
  status: DJSEMAssetStatus;
  rightsStatus: "unknown" | "owned" | "licensed" | "restricted";
  currentHash: string;
  variants: DJSEMAssetVariant[];
  versions: DJSEMAssetVersion[];
  tags: string[];
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** Input for {@link AssetManagerService.createAsset}. */
export interface CreateAssetInput { id?: string; title: string; assetType: DJSEMShowPackAssetType; origin: DJSEMAssetOrigin; payloadHashSource: unknown; rightsStatus?: DJSEMAssetRecord["rightsStatus"]; tags?: string[]; createdAt?: string; }
/** Input for {@link AssetManagerService.addVariant}. */
export interface AddVariantInput { asset: DJSEMAssetRecord; label: string; payloadHashSource: unknown; path?: string; r2Key?: string; format?: string; }
/** Input for {@link AssetManagerService.addVersion}. */
export interface AddVersionInput { asset: DJSEMAssetRecord; payloadHashSource: unknown; note?: string; createdAt?: string; }
/** Input for {@link AssetManagerService.updateStatus}. */
export interface UpdateStatusInput { asset: DJSEMAssetRecord; status: DJSEMAssetStatus; }

const _seq = new SeqCounter();
const AT = "1970-01-01T00:00:00.000Z";

// ---- service ----------------------------------------------------------------

/** Manages asset record lifecycle (creation, variants, versions, status, rights) above the show-pack packaging system. */
export class AssetManagerService {
  /**
   * Create a new draft asset record with an initial version hashed from its payload.
   * @param input - the asset's title/type/origin/rights and the payload to hash.
   * @returns the created asset record.
   */
  createAsset(input: CreateAssetInput): DJSEMAssetRecord {
    const hash = deterministicHash(input.payloadHashSource);
    const at = input.createdAt ?? AT;
    const warnings = input.rightsStatus === undefined ? ["Rights not declared; defaulting to unknown."] : [];
    return {
      id: input.id ?? `asset_${_seq.next()}`, title: input.title,
      assetType: input.assetType, origin: input.origin, status: "draft",
      rightsStatus: input.rightsStatus ?? "unknown",
      currentHash: hash,
      variants: [], versions: [{ version: 1, hash, createdAt: at }],
      tags: input.tags ?? [], warnings, createdAt: at, updatedAt: at,
    };
  }

  /**
   * Add a named variant (e.g. a different size/format) to an asset record.
   * @param input - the asset, variant label, and payload to hash.
   * @returns the asset with the new variant appended (variants sorted by id).
   */
  addVariant(input: AddVariantInput): DJSEMAssetRecord {
    const v: DJSEMAssetVariant = { id: `var_${_seq.next()}`, label: input.label, path: input.path, r2Key: input.r2Key, hash: deterministicHash(input.payloadHashSource), format: input.format };
    return { ...input.asset, variants: [...input.asset.variants, v].sort((a, b) => a.id.localeCompare(b.id)) };
  }

  /**
   * Record a new content version for an asset, updating its current hash.
   * @param input - the asset, new payload to hash, and optional note.
   * @returns the asset with the new version appended and currentHash updated.
   */
  addVersion(input: AddVersionInput): DJSEMAssetRecord {
    const hash = deterministicHash(input.payloadHashSource);
    const version = input.asset.versions.length + 1;
    return {
      ...input.asset, currentHash: hash,
      versions: [...input.asset.versions, { version, hash, createdAt: input.createdAt ?? AT, note: input.note }],
      updatedAt: input.createdAt ?? AT,
    };
  }

  /**
   * Update an asset's review status, blocking approval when rights are undeclared.
   * @param input - the asset and the new status.
   * @returns the updated asset (unchanged status + added warning if approval is blocked by unknown rights).
   */
  updateStatus(input: UpdateStatusInput): DJSEMAssetRecord {
    // approved requires declared (non-unknown) rights — rights warnings surface here.
    if (input.status === "approved" && input.asset.rightsStatus === "unknown") {
      return { ...input.asset, warnings: [...input.asset.warnings, "Cannot approve: rights unknown — declare rights first."] };
    }
    return { ...input.asset, status: input.status };
  }

  /**
   * Validate an asset record's required fields and version sequence.
   * @param asset - the asset to validate.
   * @returns validation result.
   */
  validateAsset(asset: DJSEMAssetRecord): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!asset?.id || !asset?.title) issues.push({ path: "$", message: "id and title required" });
    if (!asset?.currentHash) issues.push({ path: "$.currentHash", message: "hash required" });
    if (!["source", "generated", "uploaded"].includes(asset?.origin)) issues.push({ path: "$.origin", message: "invalid origin" });
    const versions = asset?.versions ?? [];
    for (let i = 1; i < versions.length; i++) if (versions[i].version !== versions[i - 1].version + 1) issues.push({ path: "$.versions", message: "version numbers must be sequential" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "asset_record");
  }

  /**
   * Bridge an asset record into the show-pack packaging system's asset shape (the one show-pack path).
   * @param asset - the asset record to convert.
   * @param opts - optional required flag / r2Key override.
   * @returns the show-pack asset representation.
   */
  toShowPackAsset(asset: DJSEMAssetRecord, opts?: { required?: boolean; r2Key?: string }): DJSEMShowPackAsset {
    return {
      id: asset.id, assetType: asset.assetType,
      r2Key: opts?.r2Key ?? asset.variants[0]?.r2Key,
      path: asset.variants[0]?.path,
      hash: asset.currentHash, required: !!opts?.required,
      rightsStatus: asset.rightsStatus,
      warnings: asset.rightsStatus === "unknown" ? ["Rights unknown."] : [],
    };
  }

  /**
   * Filter a list of asset records by text, type, status, and/or tag.
   * @param assets - the assets to filter.
   * @param q - the search criteria (all optional, combined with AND).
   * @returns the matching assets.
   */
  searchAssets(assets: DJSEMAssetRecord[], q: { text?: string; assetType?: DJSEMShowPackAssetType; status?: DJSEMAssetStatus; tag?: string }): DJSEMAssetRecord[] {
    return assets.filter((a) => {
      if (q.text && !a.title.toLowerCase().includes(q.text.toLowerCase())) return false;
      if (q.assetType && a.assetType !== q.assetType) return false;
      if (q.status && a.status !== q.status) return false;
      if (q.tag && !a.tags.includes(q.tag)) return false;
      return true;
    });
  }
}
